import * as XLSX from 'xlsx';
import { applyBusinessRules, mergeValidationStats } from './dataUploadBusinessRules';
import { getTemplateConfig, STANDARD_DATE_FORMAT, STANDARD_DATE_EXAMPLE } from './dataUploadTemplates';

const SLASH_DATE_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const LEGACY_TEXT_DATE_REGEX = /^([0-2]?\d|3[0-1]) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4}$/i;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Legacy column headings from older templates — still accepted on upload. */
const LEGACY_HEADER_ALIASES = {
  'Date(10 Jun 2023)': 'Date (DD/MM/YYYY)',
  'Date of birth (10 Jun 2023)': 'Date of birth (DD/MM/YYYY)',
  'date of joining(10 Jun 2023)': 'Date of joining (DD/MM/YYYY)',
  'Date of joining(10 Jun 2023)': 'Date of joining (DD/MM/YYYY)',
  'onboardingDate(10 Jun 2023)': 'Onboarding date (DD/MM/YYYY)',
  date: 'Date (DD/MM/YYYY)',
  openingBal_Date: 'Opening balance date (DD/MM/YYYY)',
  Date: 'Date (DD/MM/YYYY)',
  installment_date: 'Installment date (DD/MM/YYYY)',
};

export function xlDateToDate(xlSerial) {
  const utcDays = Math.floor(xlSerial - 25569);
  const utcValue = utcDays * 86400 * 1000;
  const dateInfo = new Date(utcValue);
  return new Date(dateInfo.getUTCFullYear(), dateInfo.getUTCMonth(), dateInfo.getUTCDate());
}

function padTwo(value) {
  return String(value).padStart(2, '0');
}

/** Convert any supported import date to DD/MM/YYYY. Returns null if invalid. */
export function normalizeImportDate(value) {
  if (value === undefined || value === null || value === '' || value === 'NA') {
    return null;
  }

  const asString = String(value).trim();

  const slashMatch = SLASH_DATE_REGEX.exec(asString);
  if (slashMatch) {
    const day = parseInt(slashMatch[1], 10);
    const month = parseInt(slashMatch[2], 10);
    const year = parseInt(slashMatch[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }
    return `${padTwo(day)}/${padTwo(month)}/${year}`;
  }

  const legacyMatch = LEGACY_TEXT_DATE_REGEX.exec(asString);
  if (legacyMatch) {
    const day = padTwo(parseInt(legacyMatch[1], 10));
    const monthIndex = MONTHS.findIndex((m) => m.toLowerCase() === legacyMatch[2].toLowerCase());
    if (monthIndex === -1) {
      return null;
    }
    const year = asString.split(' ')[2];
    return `${day}/${padTwo(monthIndex + 1)}/${year}`;
  }

  if (!Number.isNaN(Number(asString))) {
    const serialDate = xlDateToDate(Number(asString));
    return `${padTwo(serialDate.getDate())}/${padTwo(serialDate.getMonth() + 1)}/${serialDate.getFullYear()}`;
  }

  return null;
}

export function normalizeHeaderList(headers) {
  if (!Array.isArray(headers)) {
    return [];
  }
  return headers.map((header) => {
    const trimmed = String(header || '').trim();
    return LEGACY_HEADER_ALIASES[trimmed] || trimmed;
  });
}

export function headersMatch(fileHeaders, expectedHeaders) {
  const normalizedFile = normalizeHeaderList(fileHeaders);
  const normalizedExpected = normalizeHeaderList(expectedHeaders);
  if (normalizedFile.length !== normalizedExpected.length) {
    return false;
  }
  return normalizedFile.every((header, index) => header === normalizedExpected[index]);
}

export function describeHeaderMismatch(fileHeaders, expectedHeaders) {
  const normalizedFile = normalizeHeaderList(fileHeaders);
  const normalizedExpected = normalizeHeaderList(expectedHeaders);
  const mismatches = [];

  normalizedExpected.forEach((expected, index) => {
    const actual = normalizedFile[index];
    if (actual !== expected) {
      mismatches.push(`Column ${index + 1}: expected "${expected}", got "${actual || '(empty)'}"`);
    }
  });

  if (normalizedFile.length !== normalizedExpected.length) {
    mismatches.push(`Expected ${normalizedExpected.length} columns but found ${normalizedFile.length}.`);
  }

  return mismatches;
}

export function rowsToDictionaries(keys, rows) {
  return rows.map((row, rowIndex) => {
    const dictionary = { __rowNumber: rowIndex + 2 };
    keys.forEach((key, index) => {
      const cellData = row[index] !== undefined && row[index] !== '' ? row[index] : 'NA';
      dictionary[key] = cellData === 0 ? 0 : cellData;
    });
    return dictionary;
  });
}

export function buildTableColumns(keys) {
  return keys.map((key) => ({
    title: key,
    dataIndex: key,
    key,
  }));
}

export function rowMatchesSampleRow(row, headers, sampleRow) {
  if (!sampleRow || !sampleRow.length) {
    return false;
  }
  return headers.every((header, index) => String(row[header]) === String(sampleRow[index]));
}

function normalizeDateValue(value, allowExcelDateSerial) {
  if (value === undefined || value === 'NA' || value === '') {
    return { ok: true, value };
  }

  const normalized = normalizeImportDate(value);
  if (normalized) {
    return { ok: true, value: normalized };
  }

  if (allowExcelDateSerial && !Number.isNaN(Number(value))) {
    const fromSerial = normalizeImportDate(value);
    if (fromSerial) {
      return { ok: true, value: fromSerial };
    }
  }

  return {
    ok: false,
    value: String(value).trim(),
  };
}

export function normalizeDateFields(rows, dateFields, allowExcelDateSerial = true) {
  if (!dateFields || dateFields.length === 0) {
    return { rows, invalidRows: [] };
  }

  const validRows = [];
  const invalidRows = [];

  rows.forEach((row) => {
    const updatedRow = { ...row };
    let rowValid = true;
    const issues = [];

    dateFields.forEach((field) => {
      const result = normalizeDateValue(updatedRow[field], allowExcelDateSerial);
      if (!result.ok) {
        rowValid = false;
        issues.push(`${field}: "${updatedRow[field]}" is not a valid date. Use ${STANDARD_DATE_FORMAT} (example: ${STANDARD_DATE_EXAMPLE}).`);
      } else {
        updatedRow[field] = result.value;
      }
    });

    if (rowValid) {
      validRows.push(updatedRow);
    } else {
      invalidRows.push({ rowNumber: row.__rowNumber, issues });
    }
  });

  return { rows: validRows, invalidRows };
}

export function validateRequiredFields(rows, requiredFields = []) {
  if (!requiredFields.length) {
    return { rows, invalidRows: [] };
  }

  const validRows = [];
  const invalidRows = [];

  rows.forEach((row) => {
    const missing = requiredFields.filter((field) => {
      const value = row[field];
      return value === undefined || value === null || value === '' || value === 'NA';
    });

    if (missing.length) {
      invalidRows.push({
        rowNumber: row.__rowNumber,
        issues: missing.map((field) => `${field} is required`),
      });
    } else {
      validRows.push(row);
    }
  });

  return { rows: validRows, invalidRows };
}

export function stripInternalFields(rows) {
  return rows.map((row) => {
    const cleaned = { ...row };
    delete cleaned.__rowNumber;
    return cleaned;
  });
}

export function parseUploadWorksheet(worksheet, uploadType, sampleRowOverride = null) {
  const config = getTemplateConfig(uploadType);
  if (!config) {
    return { ok: false, error: 'Unknown upload type.' };
  }

  const sampleRow = sampleRowOverride || config.sampleRow || null;

  const dataAsArray = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
  if (!dataAsArray.length) {
    return { ok: false, error: 'The uploaded file is empty.' };
  }

  const keys = normalizeHeaderList(dataAsArray[0]);
  if (!headersMatch(dataAsArray[0], config.headers)) {
    const mismatches = describeHeaderMismatch(dataAsArray[0], config.headers);
    return {
      ok: false,
      error: `${config.label} template is not valid. Download the template and do not change column headings.`,
      headerMismatches: mismatches,
    };
  }

  const rawRows = dataAsArray.slice(1).filter((row) => row.some((cell) => cell !== '' && cell !== null && cell !== undefined));
  const dictionaries = rowsToDictionaries(keys, rawRows);

  const sampleSkipped = [];
  const nonSampleRows = [];
  dictionaries.forEach((row) => {
    if (rowMatchesSampleRow(row, keys, sampleRow)) {
      sampleSkipped.push(row.__rowNumber);
    } else {
      nonSampleRows.push(row);
    }
  });

  const allowExcelDateSerial = config.allowExcelDateSerial !== false;
  const dateResult = normalizeDateFields(nonSampleRows, config.dateFields, allowExcelDateSerial);
  const requiredResult = validateRequiredFields(dateResult.rows, config.requiredFields || []);

  const invalidRows = [...dateResult.invalidRows, ...requiredResult.invalidRows];
  const rowsWithMeta = requiredResult.rows;
  const businessValidation = applyBusinessRules(uploadType, rowsWithMeta);
  const datalist = stripInternalFields(rowsWithMeta);

  const baseStats = {
    totalRows: rawRows.length,
    importedRows: datalist.length,
    skippedSampleRows: sampleSkipped.length,
    skippedInvalidRows: invalidRows.length,
    invalidRows,
    sampleSkippedRows: sampleSkipped,
  };

  return {
    ok: businessValidation.ok,
    datalist,
    columns: buildTableColumns(keys),
    config,
    businessValidation,
    stats: mergeValidationStats(baseStats, businessValidation),
    error: businessValidation.ok
      ? null
      : 'Fix business rule errors below before uploading.',
  };
}

function resolveWorksheet(workbook) {
  if (workbook.SheetNames.includes('Template')) {
    return workbook.Sheets.Template;
  }
  const firstSheetName = workbook.SheetNames[0];
  return workbook.Sheets[firstSheetName];
}

export function parseUploadFile(file, uploadType, sampleRowOverride = null) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Please select an Excel file first.'));
      return;
    }

    if (!uploadType) {
      reject(new Error('Please select an upload type first.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = resolveWorksheet(workbook);
        resolve(parseUploadWorksheet(worksheet, uploadType, sampleRowOverride));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read the uploaded file.'));
    reader.readAsArrayBuffer(file);
  });
}

function buildInstructionsSheet(config, uploadType) {
  const rows = [
    ['Data import instructions'],
    ['Upload type', config.label],
    ['Date format', `${STANDARD_DATE_FORMAT} (example: ${STANDARD_DATE_EXAMPLE})`],
    ['Accepted dates', 'DD/MM/YYYY, Excel date cells, or legacy text like 01 Jan 2024'],
    ['Sample row', 'Row 2 in the Template sheet is an example only and will be auto-skipped if left in place.'],
    ['Headings', 'Do not rename, remove, or reorder columns in row 1.'],
    ['Prerequisites', config.hint || ''],
    ...(config.requiredFields && config.requiredFields.length
      ? [['Required columns', config.requiredFields.join(', ')]]
      : []),
    ['Columns', config.headers.join(' | ')],
  ];

  if (uploadType === 'loans') {
    rows.push(
      ['Path A (recommended)', 'Set Principle balance + Interest balance = still owed; Onboarding date = cutover. Skip repayments import.'],
      ['Path B', 'Leave balances at 0; import each payment via loan repayments template.'],
      ['Amount', 'Always the ORIGINAL loan amount, not the remaining balance.'],
    );
  }
  if (uploadType === 'loanschedules') {
    rows.push(
      ['When to use', 'After running loans — paste your old amortization table with exact installment dates.'],
      ['Replaces', 'Auto-generated schedule for each LoanNo in the file.'],
      ['Balance', 'Optional — remaining principle after installment; auto-calculated if blank.'],
    );
  }
  if (uploadType === 'loanrepayments') {
    rows.push(
      ['LoanId', 'Must match LoanNo from running loans — not the database ID.'],
      ['Path B only', 'Do not use if running loans already had Principle/Interest balances.'],
    );
  }

  return rows;
}

/** Fetch institution-specific sample row from the backend. */
export async function fetchUploadTemplateSample(uploadType, saccoId, backendUrl) {
  if (!uploadType || !saccoId) {
    return null;
  }
  const params = new URLSearchParams({ uploadtype: uploadType, sacco: saccoId });
  const response = await fetch(`${backendUrl}/customqueries/get_data_upload_template_sample?${params}`, {
    headers: {
      Authorization: localStorage.getItem('token') ? `Token ${localStorage.getItem('token')}` : '',
    },
  });
  if (!response.ok) {
    return null;
  }
  const payload = await response.json();
  return payload.sample_row || null;
}

/** Download .xlsx template from backend using live institution sample data. */
export async function downloadUploadTemplate(uploadType, saccoId, backendUrl) {
  const config = getTemplateConfig(uploadType);
  if (!config) {
    return { ok: false, sampleRow: null };
  }

  if (saccoId && backendUrl) {
    try {
      const params = new URLSearchParams({ sacco: saccoId });
      const response = await fetch(
        `${backendUrl}/customqueries/download_data_upload_template/${uploadType}?${params}`,
        {
          headers: {
            Authorization: localStorage.getItem('token') ? `Token ${localStorage.getItem('token')}` : '',
          },
        },
      );
      if (response.ok) {
        const blob = await response.blob();
        const samplePayload = await fetchUploadTemplateSample(uploadType, saccoId, backendUrl);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${config.filename}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return { ok: true, sampleRow: samplePayload };
      }
    } catch (err) {
      // Fall back to client-side template below.
    }
  }

  const templateRows = [config.headers];
  if (config.sampleRow) {
    templateRows.push(config.sampleRow);
  }

  const templateSheet = XLSX.utils.aoa_to_sheet(templateRows);
  const instructionsSheet = XLSX.utils.aoa_to_sheet(buildInstructionsSheet(config, uploadType));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');
  XLSX.utils.book_append_sheet(workbook, templateSheet, 'Template');
  XLSX.writeFile(workbook, `${config.filename}.xlsx`);
  return { ok: true, sampleRow: config.sampleRow || null };
}

export function formatDataSize(bytes) {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }
  return `${bytes} bytes`;
}
